const SHOP_GRID_SELECTOR = '[data-bislbox-shop-grid]';
const SHOP_ITEM_SELECTOR = ':scope > .product-grid__item';

class BislboxShopGrid extends HTMLElement {
  #grid;
  #frame;
  #mutationObserver;
  #resizeObserver;

  connectedCallback() {
    this.#mutationObserver = new MutationObserver(this.#queueUpdate);
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-curated-order'],
      childList: true,
      subtree: true,
    });

    this.#resizeObserver = new ResizeObserver(this.#queueUpdate);
    this.#queueUpdate();
  }

  disconnectedCallback() {
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();

    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #queueUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #setGrid(grid) {
    if (grid === this.#grid) return;

    this.#resizeObserver?.disconnect();
    this.#grid = grid;

    if (this.#grid) this.#resizeObserver?.observe(this.#grid);
  }

  #update() {
    this.#setGrid(this.querySelector(SHOP_GRID_SELECTOR));
    if (!this.#grid) return;

    this.#grid.setAttribute('product-grid-view', 'default');

    const items = Array.from(this.#grid.querySelectorAll(SHOP_ITEM_SELECTOR));
    for (const item of items) item.removeAttribute('data-bislbox-row-start');

    if (this.dataset.curatedOrder !== 'true' || items.length < 2) return;

    const entries = items.map((item, sourceIndex) => ({
      item,
      sourceIndex,
      familyRank: Number(item.dataset.bislboxFamilyRank),
      kind: item.dataset.bislboxBoxKind,
    }));

    const hasUnknownProduct = entries.some(
      ({ familyRank, kind }) =>
        !Number.isInteger(familyRank) || familyRank < 1 || !['primary', 'sibling'].includes(kind)
    );
    if (hasUnknownProduct) return;

    const columnCount = Number.parseInt(
      getComputedStyle(this.#grid).getPropertyValue('--bislbox-shop-columns'),
      10
    );
    const usePairedOrder = columnCount === 2;

    const desiredItems = entries
      .slice()
      .sort((left, right) => {
        const leftKindRank = left.kind === 'primary' ? 0 : 1;
        const rightKindRank = right.kind === 'primary' ? 0 : 1;

        const leftRank = usePairedOrder
          ? left.familyRank * 10 + leftKindRank
          : leftKindRank * 10 + left.familyRank;
        const rightRank = usePairedOrder
          ? right.familyRank * 10 + rightKindRank
          : rightKindRank * 10 + right.familyRank;

        return leftRank - rightRank || left.sourceIndex - right.sourceIndex;
      })
      .map(({ item }) => item);

    const orderChanged = desiredItems.some((item, index) => item !== items[index]);
    if (orderChanged) this.#grid.append(...desiredItems);

    if (columnCount === 3) {
      const primaryCount = entries.filter(({ kind }) => kind === 'primary').length;
      if (primaryCount % columnCount !== 0) {
        desiredItems.find((item) => item.dataset.bislboxBoxKind === 'sibling')?.setAttribute(
          'data-bislbox-row-start',
          ''
        );
      }
    }
  }
}

if (!customElements.get('bislbox-shop-grid')) {
  customElements.define('bislbox-shop-grid', BislboxShopGrid);
}
